/* globals Elm */
import { CONNECT_BANK_ACCOUNT_COMPLETED } from './common.mjs';

const DOM_READY = 'DomReady';
const GET_PAYMENT_TOKEN = 'GetPaymentToken';
const FOCUS_INPUT = 'FocusInput';
const CONNECT_BANK_ACCOUNT = 'ConnectBankAccount';

const KEY = 'engagecore-Key';

/**
 * @typedef {Window & { EngageCore: any }} CoreWindow
 */

/**
 * @type {CoreWindow}
 */
// @ts-ignore
const coreWindow = window;

/**
 * @typedef {(data: any, options: any, sendSub: (key: string, value: any) => void) => Promise<{ key: string, value?: any }>} HandleDomReadyFunc
 * @typedef {(paymentData: any) => Promise<{ key: string; value?: any; }>} GetTokenFunc
 * @typedef {(paymentData: any) => Promise<{ key: string; value?: any; }>} ConnectBankAccountFunc
 */

/**
 * @typedef PaymentProcessor
 * @property {string} name The name of the payment processor
 * @property {HandleDomReadyFunc} handleDomReady The handle dom ready function
 * @property {GetTokenFunc} getToken The get token function
 * @property {ConnectBankAccountFunc} [connectBankAccount] The connect bank account function
 */

/**
 *
 * @param {PaymentProcessor} paymentProcessor The payment processor
 */
export function registerPaymentProcessor(paymentProcessor) {
	coreWindow.EngageCore = coreWindow.EngageCore || {};
	coreWindow.EngageCore.PaymentProcessors =
		coreWindow.EngageCore.PaymentProcessors || {};

	coreWindow.EngageCore.PaymentProcessors[
		paymentProcessor.name.toLowerCase()
	] = paymentProcessor;
}

/**
 * @typedef StaxPaymentProcessor
 * @property {"Stax"} name The name of the payment provider
 * @property {string} staxPaymentsToken The stax payments token
 */

/**
 * @typedef StripePaymentProcessor
 * @property {"Stripe"} name The name of the payment provider
 * @property {string} stripeKey The stripe key
 */

/**
 *
 * @param src
 */
function loadScript(src) {
	return new Promise((resolve, reject) => {
		const script = document.createElement('script');

		const relativePrefix = '~/';
		if (src.startsWith(relativePrefix)) {
			src = `/${src.substring(relativePrefix.length)}`;
		}

		script.src = src;
		script.async = true;

		if (import.meta.resolve && !src.startsWith('http')) {
			script.type = 'module';
		}

		script.onload = () => resolve();
		script.onerror = () => reject(new Error(`Script load error: ${src}`));

		document.head.appendChild(script);
	});
}

/**
 * @param {StaxPaymentProcessor | StripePaymentProcessor} paymentProcessor The payment provider
 * @returns {Promise<{
 * options: object,
 * handleDomReady: HandleDomReadyFunc,
 * getToken: GetTokenFunc
 * connectBankAccount?: ConnectBankAccountFunc
 * }>} The payment provider data
 */
export async function getPaymentProcessorData(paymentProcessor) {
	let processor =
		coreWindow.EngageCore?.PaymentProcessors?.[
			paymentProcessor.name.toLowerCase()
		];

	// Try to load the scripts dynamically
	if (!processor && paymentProcessor?.scripts) {
		await Promise.all(paymentProcessor.scripts.map(loadScript));
		processor =
			coreWindow.EngageCore?.PaymentProcessors?.[
				paymentProcessor.name.toLowerCase()
			];
	}

	if (!processor) {
		throw new Error(`Payment processor ${paymentProcessor.name} not found`);
	}

	return { ...processor, options: paymentProcessor };
}

/**
 * @param {any} elmApp - The elm app
 * @param {StaxPaymentProcessor | StripePaymentProcessor} paymentProcessor The payment provider
 * @returns {Promise<{() => void}>} The cleanup function
 */
export async function initPorts(elmApp, paymentProcessor) {
	// Because loading getPaymentProcessorData is async, we queue any messages we might receive and replay them when ready
	let messageQueue = [];
	let handlePortImpl = (json) => {
		messageQueue.push(json);
	};

	const handlePort = (json) => {
		handlePortImpl(json);
	};

	elmApp.ports.out.subscribe(handlePort);

	const { options, handleDomReady, getToken, connectBankAccount } =
		await getPaymentProcessorData(paymentProcessor);

	/**
	 * @param {string} key The sub key
	 * @param {any} value The sub value
	 */
	function sendSub(key, value) {
		elmApp.ports.sub.send({
			[KEY]: key,
			value,
		});
	}

	handlePortImpl = (json) => {
		switch (json[KEY]) {
			case DOM_READY:
				handleDomReady(
					json.value,
					json.value.paymentProcessor
						? { ...options, ...json.value.paymentProcessor }
						: options,
					sendSub,
				)
					.then(({ key, value }) => sendSub(key, value))
					.catch(({ key, value }) => sendSub(key, value));
				break;
			case CONNECT_BANK_ACCOUNT:
				if (connectBankAccount) {
					connectBankAccount(json.value)
						.then(({ key, value }) => sendSub(key, value))
						.catch(({ key, value }) => sendSub(key, value));
				} else {
					sendSub(CONNECT_BANK_ACCOUNT_COMPLETED, {
						error: 'Connect bank account not supported',
					});
				}
				break;
			case GET_PAYMENT_TOKEN:
				getToken(json.value)
					.then(({ key, value }) => sendSub(key, value))
					.catch(({ key, value }) => sendSub(key, value));
				break;
			case FOCUS_INPUT:
				/** @type {HTMLIFrameElement} */ (
					document.getElementById(json.value).children[0]
				).contentWindow.focus();
				break;
			default:
				break;
		}
	};

	// Replace messages
	for (const json of messageQueue) {
		handlePortImpl(json);
	}

	messageQueue = [];

	return () => {
		elmApp.ports.out.unsubscribe(handlePort);
	};
}

/**
 * @param {{node: HTMLElement, flags: object }} options The element
 */
export function init({ node, flags }) {
	// @ts-ignore
	const elmApp = Elm.Engage.Checkout.Main.init({
		node,
		flags,
	});

	initPorts(elmApp, flags.paymentProcessor);
}
